import { ButtonGradientPrimary, Card, Colors, Heading, Icon, Text } from '@amelio/ui-library';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { AssignReviewerModal } from '../AssignReviewerModal/AssignReviewerModal';

import type { User } from '@/types/directory/User';
import type { Reviewee } from '@/utils/reviewEvaluatorSelection';

import { ReviewerNodes } from '@/components/ReviewerNodes/ReviewerNodes';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';

interface AddReviewerParams {
    revieweeId: string;
    reviewers: User[];
}

interface RemoveReviewerParams {
    revieweeId: string;
    reviewerId: string;
}

interface ReviewerSelectionProps {
    reviewees: Reviewee[];
    isLoading?: boolean;
    isSaving?: boolean;
    onAddReviewer: (params: AddReviewerParams) => void;
    onRemoveReviewer: (params: RemoveReviewerParams) => void;
}

export const ReviewerSelection = ({
    reviewees,
    isLoading,
    isSaving,
    onAddReviewer,
    onRemoveReviewer,
}: ReviewerSelectionProps) => {
    const { t } = useAmelioTranslations();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [revieweeSelected, setRevieweeSelected] = useState<User | null>(null);

    const getReviewersForReviewee = (selectedReviewee: User) => {
        const currentReviewee = reviewees.find((reviewee) => reviewee.id === selectedReviewee.id);
        return currentReviewee ? currentReviewee.reviewers : [];
    };

    const openModal = (reviewee: User) => {
        setRevieweeSelected(reviewee);
        setIsModalOpen(true);
    };

    const handleAddReviewer = ({ revieweeId, reviewers: selectedReviewers }: AddReviewerParams) => {
        if (isSaving) {
            return;
        }

        onAddReviewer({ revieweeId, reviewers: selectedReviewers });
    };

    const handleRemoveReviewer = ({ revieweeId, reviewerId }: RemoveReviewerParams) => {
        onRemoveReviewer({ revieweeId, reviewerId });
    };

    return (
        <Card
            className={'mt-40'}
            borderRadius={'2xl'}
        >
            {isLoading && (
                <Skeleton>
                    <div className={'max-w-120'}>
                        <Bone className={'h-8 w-64 rounded-lg'} />

                        <Bone className={'mt-4 h-6 w-full rounded-lg'} />
                    </div>

                    <Card className={'mt-8 bg-white'}>
                        <Bone className={'h-24 rounded-lg'} />

                        <Bone className={'my-4 h-24 rounded-lg'} />
                    </Card>

                    <div className={'my-8 flex w-full items-center justify-center'}>
                        <Bone className={'h-12 w-48 rounded-lg'} />
                    </div>
                </Skeleton>
            )}

            {!isLoading &&
                reviewees.map((reviewee) => (
                    <div key={reviewee.id}>
                        <div className={'max-w-120'}>
                            <Heading level={3}>
                                {t('evaluation360AssignReviewers.assignReviewers.title')}
                            </Heading>

                            <Text className={'mt-4'}>
                                <Trans
                                    i18nKey={
                                        'evaluation360AssignReviewers.assignReviewers.description'
                                    }
                                    values={{
                                        firstName: reviewee.firstName,
                                        lastName: reviewee.lastName,
                                    }}
                                    components={{ bold: <b /> }}
                                />
                            </Text>
                        </div>

                        <Card className={'mt-8 bg-white px-0 min-[360px]:!px-6'}>
                            <ReviewerNodes
                                reviewers={reviewee.reviewers}
                                reviewee={reviewee}
                                onRemove={(id: string) =>
                                    handleRemoveReviewer({
                                        revieweeId: reviewee.id,
                                        reviewerId: id,
                                    })
                                }
                            />
                        </Card>

                        <div className={'my-8 flex w-full items-center justify-center'}>
                            <ButtonGradientPrimary
                                leftIcon={
                                    <Icon
                                        name={'essential-add-circle'}
                                        color={Colors.WHITE}
                                        className={'-mt-1 mr-2'}
                                        size={'lg'}
                                    />
                                }
                                disabled={isSaving || isLoading}
                                onClick={() => openModal(reviewee)}
                            >
                                <Text color={Colors.WHITE}>
                                    {t('evaluation360AssignReviewers.assignReviewers.addReviewer')}
                                </Text>
                            </ButtonGradientPrimary>
                        </div>
                    </div>
                ))}

            {revieweeSelected && (
                <AssignReviewerModal
                    isOpen={isModalOpen}
                    reviewee={revieweeSelected}
                    reviewers={getReviewersForReviewee(revieweeSelected)}
                    isSaving={isSaving}
                    onAddReviewer={handleAddReviewer}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </Card>
    );
};
